import { DefaultTFuncReturn } from 'i18next'
import { FC, ReactNode, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CloseRounded } from '@mui/icons-material'
import { Box, Dialog, DialogTitle, Grid, Typography, IconButton, DialogContent } from '@mui/material'

import { GRCarousel } from '../GRCarousel/GRCarousel'
import styles from './ImageCarouselModal.module.scss'

type ImageCarouselModalProps = {
  open: boolean
  imageUrls: string[]
  onClose: () => void
  initialIndex?: number
  title?: string | DefaultTFuncReturn
  // Per-image line under the carousel, e.g. where the screenshot came from. Called
  // with the index of the visible image so the caller can key it off its own data.
  renderCaption?: (index: number) => ReactNode
  // Load the images in CORS mode. Only for URLs the app already loads with `crossOrigin` elsewhere
  // (e.g. via AnimatedImage), so the browser can't reuse a cached no-CORS response for that CORS request.
  // Left off by default: other callers show URLs (AI-generated images, ImageSlider) that may not send CORS headers.
  crossOrigin?: 'anonymous' | 'use-credentials'
}

export const ImageCarouselModal: FC<ImageCarouselModalProps> = ({ open, imageUrls = [], onClose, initialIndex, title, renderCaption, crossOrigin }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex || 0)

  const { t } = useTranslation()

  // Also resync on `open`: paging inside the modal moves `currentIndex` away from `initialIndex`,
  // so reopening on the same `initialIndex` would otherwise show the last-viewed image. A layout
  // effect so the corrected index lands before paint (no stale frame on open), and only on open
  // so the image doesn't swap while the dialog is still fading out.
  useLayoutEffect(() => {
    if (open) setCurrentIndex(initialIndex || 0)
  }, [initialIndex, open])

  const handlePrevious = () => {
    setCurrentIndex(currentIndex <= 0 ? imageUrls.length - 1 : currentIndex - 1)
  }

  const handleNext = () => {
    setCurrentIndex(currentIndex >= imageUrls.length - 1 ? 0 : currentIndex + 1)
  }

  const hasMultipleImages = imageUrls.length > 1

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth closeAfterTransition keepMounted>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item sm={4} alignSelf="center">
            <Typography variant="h3">{title}</Typography>
          </Grid>
          <Grid item xs={4} textAlign="center">
            <Typography>
              {currentIndex + 1}/{imageUrls.length}
            </Typography>
          </Grid>
          <Grid item sm={4} textAlign="right">
            <IconButton className="closeButton" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <GRCarousel
          previousButtonProps={{
            onClick: handlePrevious,
            'aria-label': t('common:label_previous'),
            disabled: !hasMultipleImages,
          }}
          nextButtonProps={{
            onClick: handleNext,
            'aria-label': t('common:label_next'),
            disabled: !hasMultipleImages,
          }}
        >
          <img src={imageUrls[currentIndex]} className={styles.image} alt="Screenshot" crossOrigin={crossOrigin} />
        </GRCarousel>
        {renderCaption ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>{renderCaption(currentIndex)}</Box> : null}
      </DialogContent>
    </Dialog>
  )
}
